import { useEffect, useState, useCallback } from 'react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { Printer, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/LanguageContext';
import { openPrintWindow } from '@/lib/printWindow';

// Escape dynamic text before interpolating into the print-window HTML.
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export default function BarcodeLabel({ value, name, sku, onPrint }) {
  const { t } = useT();
  const [barUrl, setBarUrl] = useState('');
  const [qrUrl, setQrUrl] = useState('');

  useEffect(() => {
    if (!value) return;
    try {
      const c = document.createElement('canvas');
      JsBarcode(c, String(value), { format: 'CODE128', width: 2, height: 56, fontSize: 14, margin: 6, displayValue: true });
      setBarUrl(c.toDataURL('image/png'));
    } catch { setBarUrl(''); }
    QRCode.toDataURL(String(value), { margin: 1, width: 160 }).then(setQrUrl).catch(() => setQrUrl(''));
  }, [value]);

  const printLabel = useCallback(() => {
    openPrintWindow(`<html><head><title>${esc(sku || value)}</title></head><body style="font-family:sans-serif;text-align:center;margin:0;padding:16px;">
      <div style="font-weight:700;font-size:14px;margin-bottom:6px;">${esc(name || '')}</div>
      <img src="${barUrl}" style="max-width:100%"/><br/>
      <img src="${qrUrl}" style="margin-top:8px"/>
    </body></html>`, { delay: 400 });
  }, [barUrl, qrUrl, name, sku, value]);

  const download = () => {
    if (!barUrl) return;
    const a = document.createElement('a');
    a.href = barUrl; a.download = `${sku || value}.png`; a.click();
  };

  if (!value) return null;
  return (
    <div className="rounded-lg border border-zinc-200 p-4 bg-zinc-50/50" data-testid="barcode-label">
      <div className="flex items-center justify-center gap-6 flex-wrap">
        {barUrl && <img src={barUrl} alt="barcode" className="h-14" data-testid="barcode-code128" />}
        {qrUrl && <img src={qrUrl} alt="qr" className="h-20 w-20" data-testid="barcode-qr" />}
      </div>
      <div className="flex justify-center gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={onPrint || printLabel} data-testid="barcode-print-btn" className="h-8"><Printer size={15} className="mr-1" /> {t('mfg.label.print')}</Button>
        <Button size="sm" variant="outline" onClick={download} data-testid="barcode-download-btn" className="h-8"><DownloadSimple size={15} className="mr-1" /> {t('mfg.action.download')}</Button>
      </div>
    </div>
  );
}
